import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import App from './App';
import { initSentry } from './sentry';
import { initializeInstall } from './services/install';

initializeInstall();

initSentry();

const CACHE_BUSTER = '1'; // bump on breaking schema changes

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,   // 5 minutes — cached data used longer before refetch
      gcTime: 24 * 60 * 60 * 1000, // 24 hours — keep cache in memory / persist
      retry: 1,
    },
  },
});

// Persist React Query cache to IndexedDB via idb-keyval
const asyncPersister = createAsyncStoragePersister({
  storage: {
    getItem: async (key) => {
      const value = await get(key);
      return value ?? null;
    },
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: 'leadmarka-query-cache',
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: asyncPersister,
        maxAge: 24 * 60 * 60 * 1000, // 24 hours
        buster: CACHE_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery: query => query.state.status === 'success' && query.queryKey[0] !== 'emailInbox' },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>
);

// Register after the app loads. Updates activate once existing tabs close,
// preserving unsaved work and keeping each offline shell with its own assets.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch((error) => {
        console.warn('Offline app files could not be prepared:', error);
      });
  });
}
